import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Chip, Paper, Typography } from '@mui/material';
import dashboardApi from '../../services/dashboardApi';
import { MapFeatureProperties } from '../../types/dashboard';
import { COLOR_PALETTE, getEmotionIconByPercentage, getNoVotesEmotionIcon, NO_VOTES_COLOR } from '../../utils/emotionUtils';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';
const DASHBOARD_MAP_AUTO_REFRESH_MS = 30 * 1000;

type DashboardMapProps = {
  startAt: string;
  endAt: string;
  layer: 'sentiment' | 'density';
  onAreaSelected: (feature: MapFeatureProperties | null) => void;
  actionSlot?: React.ReactNode;
  fillHeight?: boolean;
};

const numberFormat = new Intl.NumberFormat('en-US');

const DashboardMap = ({ startAt, endAt, layer, onAreaSelected, actionSlot, fillHeight = false }: DashboardMapProps) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const loadViewportDataRef = useRef<() => Promise<void>>(async () => {});
  const onAreaSelectedRef = useRef(onAreaSelected);
  const colorExpressionRef = useRef<any>(null);
  const emotionMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const requestSeqRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);

  const colorExpression = useMemo(() => {
    if (layer === 'density') {
      return [
        'interpolate',
        ['linear'],
        ['get', 'density_percent'],
        0, '#D6E9FF',
        20, '#B8D8FF',
        40, '#8AB9FF',
        65, '#4F86D9',
        100, '#0A1D45',
      ];
    }
    return ['coalesce', ['get', 'sentiment_color'], NO_VOTES_COLOR];
  }, [layer]);

  const clearEmotionMarkers = useCallback(() => {
    emotionMarkersRef.current.forEach((marker) => marker.remove());
    emotionMarkersRef.current = [];
  }, []);

  const getGeometryCenter = useCallback((geometry: any): [number, number] | null => {
    if (!geometry?.coordinates) return null;

    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;

    const consume = (coords: any) => {
      if (!Array.isArray(coords)) return;
      if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
        const [lng, lat] = coords as [number, number];
        minLng = Math.min(minLng, lng);
        minLat = Math.min(minLat, lat);
        maxLng = Math.max(maxLng, lng);
        maxLat = Math.max(maxLat, lat);
        return;
      }
      coords.forEach(consume);
    };

    consume(geometry.coordinates);
    if (!Number.isFinite(minLng) || !Number.isFinite(minLat) || !Number.isFinite(maxLng) || !Number.isFinite(maxLat)) {
      return null;
    }
    return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  }, []);

  const renderEmotionMarkers = useCallback(
    (map: mapboxgl.Map, features: any[]) => {
      clearEmotionMarkers();
      features.forEach((feature) => {
        const properties = feature.properties || {};
        const totalVotes = Number(properties.total_votes || 0);
        const positivePercentage = Number(properties.positive_percentage || 0);
        const iconSrc = totalVotes > 0 ? getEmotionIconByPercentage(positivePercentage) : getNoVotesEmotionIcon();
        if (!iconSrc) return;
        const center = getGeometryCenter(feature.geometry);
        if (!center) return;

        const markerEl = document.createElement('img');
        markerEl.src = iconSrc;
        markerEl.style.width = '22px';
        markerEl.style.height = '22px';
        markerEl.style.pointerEvents = 'none';

        const marker = new mapboxgl.Marker({ element: markerEl }).setLngLat(center as [number, number]).addTo(map);
        emotionMarkersRef.current.push(marker);
      });
    },
    [clearEmotionMarkers, getGeometryCenter]
  );

  const loadViewportData = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;

    const bounds = map.getBounds();
    if (!bounds) return;

    try {
      const requestId = ++requestSeqRef.current;
      const layerAtRequestTime = layer;
      const response = await dashboardApi.getMapData({
        startAt,
        endAt,
        zoom: map.getZoom(),
        layer: layerAtRequestTime,
        bounds: {
          minLng: bounds.getWest(),
          minLat: bounds.getSouth(),
          maxLng: bounds.getEast(),
          maxLat: bounds.getNorth(),
        },
      });

      // El usuario puede cambiar de vista o desmontar el mapa mientras llega la respuesta.
      // Validamos que el mapa siga vivo y con estilo/fuente antes de tocar getSource/setData.
      const currentMap = mapRef.current;
      if (!currentMap || currentMap !== map || !currentMap.isStyleLoaded() || requestId !== requestSeqRef.current) {
        return;
      }

      const rawFeatures = response.feature_collection?.features || [];
      const maxVotes = rawFeatures.reduce((max: number, feature: any) => {
        const votes = Number(feature?.properties?.total_votes || 0);
        return Math.max(max, votes);
      }, 0);

      const enhancedFeatureCollection = {
        ...response.feature_collection,
        features: rawFeatures.map((feature: any) => {
          const props = feature.properties || {};
          const totalVotes = Number(props.total_votes || 0);
          const goodVotes = Number(props.good_votes || 0);
          const positivePercentage = totalVotes > 0 ? (goodVotes / totalVotes) * 100 : 50;
          const densityPercent = maxVotes > 0 ? (totalVotes / maxVotes) * 100 : 0;

          return {
            ...feature,
            properties: {
              ...props,
              positive_percentage: Number(positivePercentage.toFixed(1)),
              density_percent: Number(densityPercent.toFixed(1)),
              density_label: `${densityPercent.toFixed(0)}%`,
              sentiment_color:
                totalVotes > 0
                  ? COLOR_PALETTE.find(
                      (entry) => positivePercentage >= entry.range[0] && positivePercentage < entry.range[1]
                    )?.color || '#E1D4C3'
                  : NO_VOTES_COLOR,
            },
          };
        }),
      };

      const source = currentMap.getSource('dashboard-areas') as mapboxgl.GeoJSONSource | undefined;
      if (source) {
        source.setData(enhancedFeatureCollection as any);
      }

      if (layerAtRequestTime === 'sentiment') {
        renderEmotionMarkers(currentMap, enhancedFeatureCollection.features);
      } else {
        clearEmotionMarkers();
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading dashboard map data', error);
      setIsLoading(false);
    }
  }, [startAt, endAt, layer, clearEmotionMarkers, renderEmotionMarkers]);

  useEffect(() => {
    loadViewportDataRef.current = loadViewportData;
  }, [loadViewportData]);

  useEffect(() => {
    onAreaSelectedRef.current = onAreaSelected;
  }, [onAreaSelected]);

  useEffect(() => {
    colorExpressionRef.current = colorExpression;
  }, [colorExpression]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [-73.9712, 40.7831],
      zoom: 10,
      minZoom: 7,
      maxZoom: 18,
    });

    mapRef.current = map;

    map.on('load', () => {
      map.addSource('dashboard-areas', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });

      map.addLayer({
        id: 'dashboard-areas-fill',
        type: 'fill',
        source: 'dashboard-areas',
        paint: {
          'fill-color': colorExpressionRef.current as any,
          'fill-opacity': 0.75,
        },
      });

      map.addLayer({
        id: 'dashboard-areas-outline',
        type: 'line',
        source: 'dashboard-areas',
        paint: {
          'line-color': '#dddddd',
          'line-width': 0.6,
          'line-opacity': 0.4,
        },
      });

      map.addLayer({
        id: 'dashboard-density-labels',
        type: 'symbol',
        source: 'dashboard-areas',
        layout: {
          'text-field': ['get', 'density_label'],
          'text-size': 12,
          'text-allow-overlap': true,
          visibility: 'none',
        },
        paint: {
          'text-color': '#DDE9FF',
          'text-halo-color': 'rgba(10, 18, 36, 0.8)',
          'text-halo-width': 1.2,
        },
      });

      loadViewportDataRef.current();
      window.setTimeout(() => map.resize(), 0);
    });

    const moveHandler = () => {
      window.clearTimeout((moveHandler as any)._timeout);
      (moveHandler as any)._timeout = window.setTimeout(() => {
        loadViewportDataRef.current();
      }, 350);
    };

    map.on('moveend', moveHandler);

    map.on('mousemove', 'dashboard-areas-fill', (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      const properties = feature.properties as unknown as MapFeatureProperties;

      if (!popupRef.current) {
        popupRef.current = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          className: 'area-tooltip',
        });
      }

      const confidenceLabel = properties.confidence >= 0.95 ? 'High confidence' : 'Limited data';
      const votes = Number(properties.total_votes || 0);
      const goodVotes = Number(properties.good_votes || 0);
      const badVotes = Number(properties.bad_votes || 0);
      const goodPct = votes > 0 ? ((goodVotes / votes) * 100).toFixed(1) : '0.0';
      const badPct = votes > 0 ? ((badVotes / votes) * 100).toFixed(1) : '0.0';
      const density = properties.density_percent != null ? `${Number(properties.density_percent).toFixed(1)}%` : '0.0%';
      popupRef.current
        .setLngLat(event.lngLat)
        .setHTML(
          `<div style="min-width:200px;padding:10px;color:#ffffff">
            <div style="font-weight:600;margin-bottom:6px">${properties.name}</div>
            <div>Sentiment Index: <strong>${Number(properties.sentiment_index || 0).toFixed(1)}</strong></div>
            <div>Votes: <strong>${numberFormat.format(votes)} votes</strong></div>
            <div><strong>${goodPct}%</strong> good · <strong>${badPct}%</strong> bad</div>
            <div>Vote density: <strong>${density}</strong></div>
            <div style="margin-top:4px;font-size:12px">${confidenceLabel}</div>
          </div>`
        )
        .addTo(map);
    });

    map.on('mouseleave', 'dashboard-areas-fill', () => {
      popupRef.current?.remove();
    });

    map.on('click', 'dashboard-areas-fill', (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      onAreaSelectedRef.current(feature.properties as unknown as MapFeatureProperties);
    });

    const refreshInterval = window.setInterval(() => {
      if (document.hidden) return;
      loadViewportDataRef.current();
    }, DASHBOARD_MAP_AUTO_REFRESH_MS);

    return () => {
      window.clearInterval(refreshInterval);
      popupRef.current?.remove();
      clearEmotionMarkers();
      map.remove();
      mapRef.current = null;
    };
  }, [clearEmotionMarkers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (map.getLayer('dashboard-areas-fill')) {
      map.setPaintProperty('dashboard-areas-fill', 'fill-color', colorExpression as any);
      map.setPaintProperty('dashboard-areas-fill', 'fill-opacity', layer === 'density' ? 0.62 : 0.75);
    }
    if (map.getLayer('dashboard-density-labels')) {
      map.setLayoutProperty('dashboard-density-labels', 'visibility', layer === 'density' ? 'visible' : 'none');
    }
    if (layer === 'density') {
      clearEmotionMarkers();
    }
    loadViewportData();
  }, [colorExpression, loadViewportData, layer, startAt, endAt, clearEmotionMarkers]);

  return (
    <Paper
      className="dashboard-section-card"
      sx={{ p: 2, borderRadius: 2, height: fillHeight ? '100%' : 'auto', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="h6" sx={{ color: '#e8edf4' }}>Map Drilldown</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {actionSlot}
          <Chip
            size="small"
            color={layer === 'sentiment' ? 'success' : 'primary'}
            label={layer === 'sentiment' ? 'Sentiment Layer' : 'Vote Density Layer'}
          />
        </Box>
      </Box>
      <Box
        ref={mapContainerRef}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          height: fillHeight ? '100%' : 480,
          minHeight: fillHeight ? 0 : 480,
          flex: fillHeight ? 1 : 'unset',
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.10)',
          opacity: isLoading ? 0.65 : 1,
        }}
      />
    </Paper>
  );
};

export default DashboardMap;
